import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, OverlayTrigger, Pagination, Row, Spinner, Table, Tooltip as BootstrapTooltip } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { FiInfo } from 'react-icons/fi';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { fileAPI } from '../utils/api';
import { CHART_COLORS } from '../utils/chartColors';
import './DashboardPage.css';

const FINANCIAL_YEAR_START_MONTH_INDEX = 3;
const MONTH_BLOCK_ALERT_THRESHOLD = 10000000;
const PARTY_CHART_TOP_LIMIT = 8;

const normalizeText = (value) => String(value || '').trim();

const buildTopNWithOthers = (items, limit = 8, nameKey = 'name', valueKey = 'amount') => {
  if (!Array.isArray(items) || !items.length) return [];
  const topItems = items.slice(0, limit);
  const otherValue = items.slice(limit).reduce((sum, item) => sum + (Number(item?.[valueKey]) || 0), 0);
  if (otherValue <= 0) return topItems;
  return [...topItems, { [nameKey]: 'Others', [valueKey]: otherValue }];
};

const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/,/g, '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseEmdDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const text = String(value).trim();
  const directDate = new Date(text);
  if (!Number.isNaN(directDate.getTime())) return directDate;

  const dotDate = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (dotDate) {
    const day = Number(dotDate[1]);
    const month = Number(dotDate[2]);
    let year = Number(dotDate[3]);
    if (year < 100) year += 2000;
    const date = new Date(year, month - 1, day);
    if (!Number.isNaN(date.getTime())) return date;
  }

  const monthYear = text.match(/^([A-Za-z]{3})\s+(\d{2,4})$/);
  if (monthYear) {
    const monthMap = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
    const month = monthMap[monthYear[1].toLowerCase()];
    let year = Number(monthYear[2]);
    if (year < 100) year += 2000;
    const date = new Date(year, month, 1);
    if (!Number.isNaN(date.getTime())) return date;
  }

  return null;
};

const formatAmount = (value) => {
  const amount = parseAmount(value);
  if (!Number.isFinite(amount)) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatCompactAmount = (value) => {
  const amount = parseAmount(value);
  if (!Number.isFinite(amount)) return '-';
  return new Intl.NumberFormat('en-IN', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2
  }).format(amount);
};

const formatDate = (value) => {
  const date = parseEmdDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getFinancialYearLabel = (date) => {
  if (!date) return 'Unknown';
  const year = date.getFullYear();
  const month = date.getMonth();
  const startYear = month >= FINANCIAL_YEAR_START_MONTH_INDEX ? year : year - 1;
  const endYearShort = String(startYear + 1).slice(-2);
  return `FY${String(startYear).slice(-2)}-${endYearShort}`;
};

const getRefundStatus = (remarks) => {
  const text = normalizeText(remarks).toLowerCase();
  if (!text) return 'Pending';
  if (text.includes('adjust') || text.includes('utiliz')) return 'Adjusted / Utilized';
  if (text.includes('refund') || text.includes('refundable')) return 'Refundable';
  return 'Pending';
};

const normalizeCashEmdRow = (row, index) => {
  const date = parseEmdDate(row.docDate) || parseEmdDate(row.docMonth);
  const amount = parseAmount(row.amount);
  const finalName = normalizeText(row.finalName) || 'Unknown';
  const bidProject = normalizeText(row.bidProject) || 'Unknown';
  const sapDocNo = normalizeText(row.sapDocNo) || `ROW-${index + 1}`;
  const remarks = normalizeText(row.remarks);
  const glCode = normalizeText(row.glCode) || 'Unknown';
  const refundStatus = getRefundStatus(remarks);
  const fy = getFinancialYearLabel(date);
  const monthKey = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` : 'Unknown';
  const monthLabel = date ? date.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }) : 'Unknown';

  return {
    ...row,
    amount,
    parsedDate: date,
    finalName,
    bidProject,
    sapDocNo,
    remarks,
    glCode,
    refundStatus,
    fy,
    monthKey,
    monthLabel
  };
};

const normalizeCashEmdRows = (rows) => (rows || []).map((row, index) => normalizeCashEmdRow(row, index));

const computePrevFinancialYear = (currentFY) => {
  const prevFYStartYear = Number(currentFY.slice(2, 4)) - 1;
  return `FY${String(prevFYStartYear).padStart(2, '0')}-${String(prevFYStartYear + 1).padStart(2, '0')}`;
};

const computeRefundableAmount = (scopedRows) => scopedRows
  .filter((row) => {
    const remarksLower = row.remarks.toLowerCase();
    return remarksLower.includes('refundable') || remarksLower.includes('refund') || remarksLower.includes('emd');
  })
  .reduce((sum, row) => sum + row.amount, 0);

const buildMonthlyTrend = (scopedRows) => {
  const monthMap = {};
  scopedRows.forEach((row) => {
    if (!monthMap[row.monthKey]) monthMap[row.monthKey] = { monthKey: row.monthKey, month: row.monthLabel, amount: 0, count: 0 };
    monthMap[row.monthKey].amount += row.amount;
    monthMap[row.monthKey].count += 1;
  });
  return Object.values(monthMap).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
};

const buildCashEmdAggregations = (scopedRows) => {
  const partyMap = {};
  const projectMap = {};
  const projectCountMap = {};
  const refundMap = {};
  const glAmountMap = {};
  const glCountMap = {};

  scopedRows.forEach((row) => {
    partyMap[row.finalName] = (partyMap[row.finalName] || 0) + row.amount;
    projectMap[row.bidProject] = (projectMap[row.bidProject] || 0) + row.amount;
    projectCountMap[row.bidProject] = (projectCountMap[row.bidProject] || 0) + 1;
    refundMap[row.refundStatus] = (refundMap[row.refundStatus] || 0) + row.amount;
    glAmountMap[row.glCode] = (glAmountMap[row.glCode] || 0) + row.amount;
    glCountMap[row.glCode] = (glCountMap[row.glCode] || 0) + 1;
  });

  const partyAmountData = Object.entries(partyMap).map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount);
  const projectAmountData = Object.entries(projectMap).map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount).slice(0, 10);
  const projectCountData = Object.entries(projectCountMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10);
  const refundSplitData = Object.entries(refundMap).map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount);
  const glAmountData = Object.entries(glAmountMap).map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount);
  const glCountData = Object.entries(glCountMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);

  const topParties = partyAmountData.slice(0, 5);
  const partyAmountChartData = buildTopNWithOthers(partyAmountData, PARTY_CHART_TOP_LIMIT, 'name', 'amount');
  const topPartyNames = Array.from(new Set(partyAmountData.slice(0, 5).map((item) => item.name)));

  return {
    partyAmountData,
    partyAmountChartData,
    topParties,
    projectAmountData,
    projectCountData,
    refundSplitData,
    glAmountData,
    glCountData,
    topPartyNames
  };
};

const buildFyPartyData = ({ normalizedRows, topPartyNames }) => {
  const fyPartyBase = {};
  normalizedRows.forEach((row) => {
    if (!topPartyNames.includes(row.finalName)) return;
    if (!fyPartyBase[row.fy]) fyPartyBase[row.fy] = { fy: row.fy };
    fyPartyBase[row.fy][row.finalName] = (fyPartyBase[row.fy][row.finalName] || 0) + row.amount;
  });
  return Object.values(fyPartyBase).sort((a, b) => a.fy.localeCompare(b.fy));
};

const computeCashEmdAnalytics = ({ rows, fyFilter }) => {
  const normalizedRows = normalizeCashEmdRows(rows);
  const fyOptions = Array.from(new Set(normalizedRows.map((row) => row.fy))).sort((a, b) => a.localeCompare(b));
  const scopedRows = fyFilter === 'all' ? normalizedRows : normalizedRows.filter((row) => row.fy === fyFilter);

  const currentFY = getFinancialYearLabel(new Date());
  const prevFY = computePrevFinancialYear(currentFY);

  const totalAmount = scopedRows.reduce((sum, row) => sum + row.amount, 0);
  const totalDocs = scopedRows.length;
  const refundableAmount = computeRefundableAmount(scopedRows);
  const activeBidsCount = new Set(scopedRows.map((row) => row.bidProject)).size;
  const uniquePartiesCount = new Set(scopedRows.map((row) => row.finalName)).size;
  const currentFyAmount = normalizedRows.filter((row) => row.fy === currentFY).reduce((sum, row) => sum + row.amount, 0);

  const monthlyTrend = buildMonthlyTrend(scopedRows);
  const aggregation = buildCashEmdAggregations(scopedRows);
  const fyPartyData = buildFyPartyData({ normalizedRows, topPartyNames: aggregation.topPartyNames });

  const monthAboveThreshold = monthlyTrend.filter((item) => item.amount > MONTH_BLOCK_ALERT_THRESHOLD);
  const pendingOver6MonthsRows = normalizedRows.filter((row) => {
    if (row.refundStatus !== 'Pending' || !row.parsedDate) return false;
    const days = (Date.now() - row.parsedDate.getTime()) / (1000 * 60 * 60 * 24);
    return days > 180;
  });
  const pendingOver6Months = pendingOver6MonthsRows.length;
  const pendingOver6MonthsAmount = pendingOver6MonthsRows.reduce((sum, row) => sum + row.amount, 0);

  const currentFYTotal = normalizedRows.filter((row) => row.fy === currentFY).reduce((sum, row) => sum + row.amount, 0);
  const prevFYTotal = normalizedRows.filter((row) => row.fy === prevFY).reduce((sum, row) => sum + row.amount, 0);
  const yoyGrowthPct = prevFYTotal > 0 ? ((currentFYTotal - prevFYTotal) / prevFYTotal) * 100 : null;

  const avgPerBid = activeBidsCount > 0 ? totalAmount / activeBidsCount : 0;
  const partyOptions = Array.from(new Set(scopedRows.map((row) => row.finalName))).sort((a, b) => a.localeCompare(b));
  const projectOptions = Array.from(new Set(scopedRows.map((row) => row.bidProject))).sort((a, b) => a.localeCompare(b));

  return {
    scopedRows,
    fyOptions,
    totalAmount,
    totalDocs,
    refundableAmount,
    activeBidsCount,
    uniquePartiesCount,
    currentFyAmount,
    monthlyTrend,
    partyAmountData: aggregation.partyAmountData,
    partyAmountChartData: aggregation.partyAmountChartData,
    topParties: aggregation.topParties,
    projectAmountData: aggregation.projectAmountData,
    projectCountData: aggregation.projectCountData,
    refundSplitData: aggregation.refundSplitData,
    glAmountData: aggregation.glAmountData,
    glCountData: aggregation.glCountData,
    fyPartyData,
    topPartyNames: aggregation.topPartyNames,
    monthAboveThreshold,
    highBlockMonthCount: monthAboveThreshold.length,
    pendingOver6Months,
    pendingOver6MonthsAmount,
    yoyGrowthPct,
    yoyAmountChange: currentFYTotal - prevFYTotal,
    avgPerBid,
    partyOptions,
    projectOptions,
    currentFY
  };
};

const matchesCashEmdActiveDataFilter = (row, activeDataFilter) => {
  switch (activeDataFilter.type) {
    case 'refundable': {
      const remarksLower = row.remarks.toLowerCase();
      return remarksLower.includes('refundable') || remarksLower.includes('refund') || remarksLower.includes('emd');
    }
    case 'hasBid':
      return row.bidProject !== 'Unknown';
    case 'party':
      return row.finalName === activeDataFilter.value;
    case 'project':
      return row.bidProject === activeDataFilter.value;
    case 'refundStatus':
      return row.refundStatus === activeDataFilter.value;
    case 'gl':
      return row.glCode === activeDataFilter.value;
    case 'month':
      return row.monthKey === activeDataFilter.value;
    case 'fy':
      return row.fy === activeDataFilter.value;
    default:
      return true;
  }
};

const computeCashEmdDrillRows = ({
  scopedRows,
  activeDataFilter,
  tablePartyFilter,
  tableProjectFilter,
  amountMin,
  amountMax,
  searchTerm
}) => {
  const min = amountMin === '' ? null : Number(amountMin);
  const max = amountMax === '' ? null : Number(amountMax);
  const term = searchTerm.trim().toLowerCase();

  return scopedRows.filter((row) => {
    if (!matchesCashEmdActiveDataFilter(row, activeDataFilter)) return false;
    if (tablePartyFilter !== 'all' && row.finalName !== tablePartyFilter) return false;
    if (tableProjectFilter !== 'all' && row.bidProject !== tableProjectFilter) return false;
    if (min !== null && Number.isFinite(min) && row.amount < min) return false;
    if (max !== null && Number.isFinite(max) && row.amount > max) return false;
    if (!term) return true;
    return [row.sapDocNo, row.finalName, row.bidProject, row.remarks, row.glCode, row.fy].join(' ').toLowerCase().includes(term);
  });
};

const INITIAL_CASH_EMD_FORM = {
  sapDocNo: '',
  glCode: '',
  docDate: '',
  docMonth: '',
  amount: '',
  finalName: '',
  bidProject: '',
  remarks: '',
  icici257AccountReceipt: '',
  clearDate: '',
  clearDocNo: ''
};

const CashEmdActiveDataFilterPropType = PropTypes.shape({
  type: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.any
});

const CashEmdAnalyticsPropType = PropTypes.shape({
  fyOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  highBlockMonthCount: PropTypes.number,
  pendingOver6Months: PropTypes.number,
  pendingOver6MonthsAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  yoyGrowthPct: PropTypes.number,
  avgPerBid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  monthlyTrend: PropTypes.arrayOf(PropTypes.shape({
    month: PropTypes.string,
    monthKey: PropTypes.string,
    amount: PropTypes.number
  })),
  partyAmountChartData: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    amount: PropTypes.number
  })),
  topParties: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    amount: PropTypes.number,
    count: PropTypes.number
  })),
  projectAmountData: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    amount: PropTypes.number
  })),
  projectCountData: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    count: PropTypes.number
  })),
  refundSplitData: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    amount: PropTypes.number,
    count: PropTypes.number
  })),
  glAmountData: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    amount: PropTypes.number
  })),
  glCountData: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    count: PropTypes.number
  })),
  fyPartyData: PropTypes.arrayOf(PropTypes.object),
  topPartyNames: PropTypes.arrayOf(PropTypes.string),
  partyOptions: PropTypes.arrayOf(PropTypes.string),
  projectOptions: PropTypes.arrayOf(PropTypes.string)
}).isRequired;

const CashEmdAddFormDataPropType = PropTypes.shape({
  sapDocNo: PropTypes.string,
  glCode: PropTypes.string,
  docDate: PropTypes.string,
  docMonth: PropTypes.string,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  finalName: PropTypes.string,
  bidProject: PropTypes.string,
  remarks: PropTypes.string,
  icici257AccountReceipt: PropTypes.string,
  clearDate: PropTypes.string,
  clearDocNo: PropTypes.string
});

const CashEmdFiltersRow = ({ fyFilter, onChangeFy, analytics }) => (
  <Row className="g-3 mb-3">
    <Col md={4} lg={2}>
      <Form.Select value={fyFilter} onChange={(e) => onChangeFy(e.target.value)}>
        <option value="all">All FY</option>
        {analytics.fyOptions.map((fy) => (<option key={fy} value={fy}>{fy}</option>))}
      </Form.Select>
    </Col>
    <Col md={8} lg={10} className="d-flex flex-wrap gap-2">
      <OverlayTrigger placement="top" overlay={<BootstrapTooltip id="cash-tip-high-block">Number of months where monthly blocked amount exceeded ₹1 Cr.</BootstrapTooltip>}>
        <Badge bg={analytics.highBlockMonthCount ? 'danger' : 'secondary'}>{`High Cash Block Months: ${analytics.highBlockMonthCount}`}</Badge>
      </OverlayTrigger>
      <OverlayTrigger placement="top" overlay={<BootstrapTooltip id="cash-tip-pending-6m">Pending refund cases older than 180 days from entry date. Amount shown is SUM(Amount) for those cases.</BootstrapTooltip>}>
        <Badge bg={analytics.pendingOver6Months ? 'warning' : 'secondary'} text="dark">{`Long-Pending Refund Cases: ${analytics.pendingOver6Months} (${formatCompactAmount(analytics.pendingOver6MonthsAmount)})`}</Badge>
      </OverlayTrigger>
      <OverlayTrigger placement="top" overlay={<BootstrapTooltip id="cash-tip-yoy">((Current FY total - Previous FY total) / Previous FY total) x 100.</BootstrapTooltip>}>
        <Badge bg={analytics.yoyGrowthPct !== null && analytics.yoyGrowthPct > 0 ? 'danger' : 'success'}>
          {`Cash Blocking Change vs Last FY: ${analytics.yoyGrowthPct === null ? 'NA' : `${analytics.yoyGrowthPct >= 0 ? '+' : ''}${analytics.yoyGrowthPct.toFixed(1)}%`}`}
        </Badge>
      </OverlayTrigger>
      <OverlayTrigger placement="top" overlay={<BootstrapTooltip id="cash-tip-avg-bid">Average blocked cash per unique BID Project = Total Amount / Distinct BID Project.</BootstrapTooltip>}>
        <Badge bg="primary">{`Average Cash Block per Bid: ${formatCompactAmount(analytics.avgPerBid)}`}</Badge>
      </OverlayTrigger>
    </Col>
  </Row>
);

CashEmdFiltersRow.propTypes = {
  fyFilter: PropTypes.string.isRequired,
  onChangeFy: PropTypes.func.isRequired,
  analytics: CashEmdAnalyticsPropType
};

const CashEmdActiveDataFilterBanner = ({ activeDataFilter, onClear }) => {
  if (activeDataFilter.type === 'all') return null;

  return (
    <div className="d-flex justify-content-between align-items-center mb-2">
      <div className="d-inline-flex align-items-center gap-2 bg-info-subtle border border-info-subtle text-info-emphasis rounded-pill px-3 py-1 small">
        <strong>Data Filter:</strong>
        <span>{activeDataFilter.label}</span>
      </div>
      <Button size="sm" variant="outline-primary" className="py-1 px-2" onClick={onClear}>Clear</Button>
    </div>
  );
};

CashEmdActiveDataFilterBanner.propTypes = {
  activeDataFilter: CashEmdActiveDataFilterPropType.isRequired,
  onClear: PropTypes.func.isRequired
};

const CashEmdKpiCardPropType = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  filterType: PropTypes.string.isRequired,
  filterValue: PropTypes.any
});

const CashEmdKpiCard = ({ card, isSelected, onSelect }) => (
  <Card
    className={`shadow-sm h-100 ${isSelected ? 'border border-primary border-2' : ''}`}
    style={{ cursor: 'pointer' }}
    onClick={onSelect}
  >
    <Card.Body>
      <p className="text-muted mb-1 d-flex align-items-center gap-1">
        {card.title}
        <OverlayTrigger placement="top" overlay={<BootstrapTooltip id={`cash-kpi-tip-${card.key}`}>{card.tooltip}</BootstrapTooltip>}>
          <button
            type="button"
            className="btn btn-link p-0 border-0 d-inline-flex align-items-center justify-content-center rounded-circle border text-secondary"
            style={{ width: 16, height: 16, lineHeight: 1 }}
            aria-label={`Info about ${card.title}`}
            onClick={(e) => e.stopPropagation()}
          >
            <FiInfo size={10} />
          </button>
        </OverlayTrigger>
      </p>
      <h5 className="mb-0">{card.value}</h5>
    </Card.Body>
  </Card>
);

CashEmdKpiCard.propTypes = {
  card: CashEmdKpiCardPropType.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired
};

const CashEmdKpiGrid = ({ kpiCards, activeDataFilter, onApplyDataFilter }) => (
  <Row className="g-3 mb-3 row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-6">
    {kpiCards.map((card) => {
      const isSelected = activeDataFilter.type === card.filterType && String(activeDataFilter.value) === String(card.filterValue);
      return (
        <Col key={card.key}>
          <CashEmdKpiCard card={card} isSelected={isSelected} onSelect={() => onApplyDataFilter(card.filterType, card.filterValue, card.title)} />
        </Col>
      );
    })}
  </Row>
);

CashEmdKpiGrid.propTypes = {
  kpiCards: PropTypes.arrayOf(CashEmdKpiCardPropType).isRequired,
  onApplyDataFilter: PropTypes.func.isRequired,
  activeDataFilter: CashEmdActiveDataFilterPropType.isRequired
};

const CashEmdTrendSection = ({ analytics, isActive, onApplyDataFilter }) => (
  <Row className="g-3 mb-3">
    <Col lg={6}>
      <Card className={`shadow-sm h-100 ${isActive ? 'border border-primary border-2' : ''}`}>
        <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Amount Trend Over Time</h6></Card.Header>
        <Card.Body style={{ minHeight: 320 }}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart
              data={analytics.monthlyTrend}
              onClick={(state) => {
                const p = state?.activePayload?.[0]?.payload;
                if (p?.monthKey) onApplyDataFilter('month', p.monthKey, `Month: ${p.month}`);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={formatCompactAmount} />
              <Tooltip formatter={(value) => formatAmount(value)} />
              <Line type="monotone" dataKey="amount" stroke="#2563eb" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
    </Col>
    <Col lg={6}>
      <Card className={`shadow-sm h-100 ${isActive ? 'border border-primary border-2' : ''}`}>
        <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Monthly Amount Contribution</h6></Card.Header>
        <Card.Body style={{ minHeight: 320 }}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={analytics.monthlyTrend}
              onClick={(state) => {
                const p = state?.activePayload?.[0]?.payload;
                if (p?.monthKey) onApplyDataFilter('month', p.monthKey, `Month: ${p.month}`);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={formatCompactAmount} />
              <Tooltip formatter={(value) => formatAmount(value)} />
              <Bar dataKey="amount" fill="#16a34a" />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

CashEmdTrendSection.propTypes = {
  analytics: CashEmdAnalyticsPropType,
  isActive: PropTypes.bool,
  onApplyDataFilter: PropTypes.func.isRequired
};

const CashEmdPartySection = ({ analytics, isActive, onApplyDataFilter }) => (
  <Row className="g-3 mb-3">
    <Col lg={6}>
      <Card className={`shadow-sm h-100 ${isActive ? 'border border-primary border-2' : ''}`}>
        <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Amount by Final Name (Top {PARTY_CHART_TOP_LIMIT} + Others)</h6></Card.Header>
        <Card.Body style={{ minHeight: 320 }}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={analytics.partyAmountChartData}
              layout="vertical"
              margin={{ top: 10, right: 20, left: 80, bottom: 10 }}
              onClick={(state) => {
                const name = state?.activeLabel;
                if (name && name !== 'Others') onApplyDataFilter('party', name, `Party: ${name}`);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={formatCompactAmount} />
              <YAxis type="category" dataKey="name" width={130} />
              <Tooltip formatter={(value) => formatAmount(value)} />
              <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                {analytics.partyAmountChartData.map((entry, index) => (
                  <Cell key={`${entry.name}-${index}`} fill={entry.name === 'Others' ? '#94a3b8' : CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
    </Col>
    <Col lg={6}>
      <Card className={`shadow-sm h-100 ${isActive ? 'border border-primary border-2' : ''}`}>
        <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Top 5 Parties by Amount</h6></Card.Header>
        <Card.Body style={{ minHeight: 320 }}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={analytics.topParties}
              onClick={(state) => {
                const name = state?.activeLabel;
                if (name) onApplyDataFilter('party', name, `Party: ${name}`);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={formatCompactAmount} />
              <Tooltip formatter={(value) => formatAmount(value)} />
              <Bar dataKey="amount" fill="#9333ea" />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

CashEmdPartySection.propTypes = {
  analytics: CashEmdAnalyticsPropType,
  isActive: PropTypes.bool,
  onApplyDataFilter: PropTypes.func.isRequired
};

const CashEmdProjectSection = ({ analytics, isActive, onApplyDataFilter }) => (
  <Row className="g-3 mb-3">
    <Col lg={6}>
      <Card className={`shadow-sm h-100 ${isActive ? 'border border-primary border-2' : ''}`}>
        <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Amount by BID Project</h6></Card.Header>
        <Card.Body style={{ minHeight: 320 }}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={analytics.projectAmountData}
              layout="vertical"
              margin={{ left: 80 }}
              onClick={(state) => {
                const name = state?.activeLabel;
                if (name) onApplyDataFilter('project', name, `Project: ${name}`);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={formatCompactAmount} />
              <YAxis type="category" dataKey="name" width={180} />
              <Tooltip formatter={(value) => formatAmount(value)} />
              <Bar dataKey="amount" fill="#0ea5e9" />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
    </Col>
    <Col lg={6}>
      <Card className={`shadow-sm h-100 ${isActive ? 'border border-primary border-2' : ''}`}>
        <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Entries per Project</h6></Card.Header>
        <Card.Body style={{ minHeight: 320 }}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={analytics.projectCountData}
              onClick={(state) => {
                const name = state?.activeLabel;
                if (name) onApplyDataFilter('project', name, `Project: ${name}`);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#64748b" />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

CashEmdProjectSection.propTypes = {
  analytics: CashEmdAnalyticsPropType,
  isActive: PropTypes.bool,
  onApplyDataFilter: PropTypes.func.isRequired
};

const CashEmdRefundAndGlSection = ({ analytics, isRefundActive, isGlActive, isFyActive, onApplyDataFilter }) => (
  <>
    <Row className="g-3 mb-3">
      <Col lg={6}>
        <Card className={`shadow-sm h-100 ${isRefundActive ? 'border border-primary border-2' : ''}`}>
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">Refund Status Split</h6></Card.Header>
          <Card.Body style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={analytics.refundSplitData}
                  dataKey="amount"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  onClick={(entry) => {
                    if (entry?.name) onApplyDataFilter('refundStatus', entry.name, `Refund Status: ${entry.name}`);
                  }}
                >
                  {analytics.refundSplitData.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatAmount(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>
      <Col lg={6}>
        <Card className={`shadow-sm h-100 ${isGlActive ? 'border border-primary border-2' : ''}`}>
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">GL Code Analysis (Amount)</h6></Card.Header>
          <Card.Body style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={analytics.glAmountData}
                onClick={(state) => {
                  const code = state?.activeLabel;
                  if (code) onApplyDataFilter('gl', code, `GL Code: ${code}`);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={formatCompactAmount} />
                <Tooltip formatter={(value) => formatAmount(value)} />
                <Bar dataKey="amount" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>
    </Row>

    <Row className="g-3 mb-3">
      <Col lg={6}>
        <Card className={`shadow-sm h-100 ${isGlActive ? 'border border-primary border-2' : ''}`}>
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">GL Code Analysis (Count)</h6></Card.Header>
          <Card.Body style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={analytics.glCountData}
                onClick={(state) => {
                  const code = state?.activeLabel;
                  if (code) onApplyDataFilter('gl', code, `GL Code: ${code}`);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#dc2626" />
              </BarChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>
      <Col lg={6}>
        <Card className={`shadow-sm h-100 ${isFyActive ? 'border border-primary border-2' : ''}`}>
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">FY vs Amount by Top Parties</h6></Card.Header>
          <Card.Body style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={analytics.fyPartyData}
                onClick={(state) => {
                  const fy = state?.activeLabel;
                  if (fy) onApplyDataFilter('fy', fy, `Financial Year: ${fy}`);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="fy" />
                <YAxis tickFormatter={formatCompactAmount} />
                <Tooltip formatter={(value) => formatAmount(value)} />
                <Legend />
                {analytics.topPartyNames.map((party, index) => (
                  <Bar key={party} dataKey={party} stackId="fy" fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  </>
);

CashEmdRefundAndGlSection.propTypes = {
  analytics: CashEmdAnalyticsPropType,
  isRefundActive: PropTypes.bool,
  isGlActive: PropTypes.bool,
  isFyActive: PropTypes.bool,
  onApplyDataFilter: PropTypes.func.isRequired
};

const CashEmdAddEntryCard = ({
  showAddForm,
  setShowAddForm,
  addFormError,
  addFormSuccess,
  addFormData,
  onChangeField,
  onSubmit,
  addFormSubmitting,
  onResetMessages
}) => (
  <Card className="shadow-sm mb-3">
    <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
      <h5 className="mb-0">Add Cash EMD Entry</h5>
      <Button
        size="sm"
        variant={showAddForm ? 'outline-secondary' : 'primary'}
        onClick={() => {
          setShowAddForm((prev) => !prev);
          onResetMessages();
        }}
      >
        {showAddForm ? 'Hide Form' : 'Add Entry'}
      </Button>
    </Card.Header>
    {showAddForm && (
      <Card.Body>
        {addFormError && <Alert variant="danger" className="mb-3">{addFormError}</Alert>}
        {addFormSuccess && <Alert variant="success" className="mb-3">{addFormSuccess}</Alert>}
        <Form onSubmit={onSubmit}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label className="mb-1">SAP Doc No</Form.Label>
              <Form.Control value={addFormData.sapDocNo} onChange={(e) => onChangeField('sapDocNo', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">GL Code</Form.Label>
              <Form.Control value={addFormData.glCode} onChange={(e) => onChangeField('glCode', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Date</Form.Label>
              <Form.Control type="date" value={addFormData.docDate} onChange={(e) => onChangeField('docDate', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Month</Form.Label>
              <Form.Control value={addFormData.docMonth} onChange={(e) => onChangeField('docMonth', e.target.value)} placeholder="Apr 25" />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Amount</Form.Label>
              <Form.Control type="number" step="0.01" value={addFormData.amount} onChange={(e) => onChangeField('amount', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Final Name</Form.Label>
              <Form.Control value={addFormData.finalName} onChange={(e) => onChangeField('finalName', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">BID Project</Form.Label>
              <Form.Control value={addFormData.bidProject} onChange={(e) => onChangeField('bidProject', e.target.value)} />
            </Col>
            <Col md={8}>
              <Form.Label className="mb-1">Remarks</Form.Label>
              <Form.Control value={addFormData.remarks} onChange={(e) => onChangeField('remarks', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">ICICI 257 Account Receipt</Form.Label>
              <Form.Control value={addFormData.icici257AccountReceipt} onChange={(e) => onChangeField('icici257AccountReceipt', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Clear Date</Form.Label>
              <Form.Control type="date" value={addFormData.clearDate} onChange={(e) => onChangeField('clearDate', e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="mb-1">Clear Doc No</Form.Label>
              <Form.Control value={addFormData.clearDocNo} onChange={(e) => onChangeField('clearDocNo', e.target.value)} />
            </Col>
          </Row>
          <div className="mt-3 d-flex justify-content-end">
            <Button type="submit" disabled={addFormSubmitting}>
              {addFormSubmitting ? 'Saving...' : 'Save Entry'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    )}
  </Card>
);

CashEmdAddEntryCard.propTypes = {
  showAddForm: PropTypes.bool,
  setShowAddForm: PropTypes.func.isRequired,
  addFormError: PropTypes.string,
  addFormSuccess: PropTypes.string,
  addFormData: CashEmdAddFormDataPropType.isRequired,
  onChangeField: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  addFormSubmitting: PropTypes.bool,
  onResetMessages: PropTypes.func.isRequired
};

const CashEmdTableCard = ({
  analytics,
  tablePartyFilter,
  onChangeTablePartyFilter,
  tableProjectFilter,
  onChangeTableProjectFilter,
  amountMin,
  onChangeAmountMin,
  amountMax,
  onChangeAmountMax,
  searchTerm,
  onChangeSearchTerm,
  drillRows,
  paginatedRows,
  currentPageSafe,
  pageSize,
  totalPages,
  onSetPageSize,
  onSetCurrentPage
}) => (
  <Card className="shadow-sm">
    <Card.Header className="bg-white border-bottom"><h5 className="mb-0">Cash EMD Table</h5></Card.Header>
    <Card.Body>
      <Row className="g-2 mb-3">
        <Col md={2}>
          <Form.Select value={tablePartyFilter} onChange={(e) => onChangeTablePartyFilter(e.target.value)}>
            <option value="all">All Parties</option>
            {analytics.partyOptions.map((party) => <option key={party} value={party}>{party}</option>)}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select value={tableProjectFilter} onChange={(e) => onChangeTableProjectFilter(e.target.value)}>
            <option value="all">All Projects</option>
            {analytics.projectOptions.map((project) => <option key={project} value={project}>{project}</option>)}
          </Form.Select>
        </Col>
        <Col md={2}><Form.Control type="number" value={amountMin} onChange={(e) => onChangeAmountMin(e.target.value)} placeholder="Min Amount" /></Col>
        <Col md={2}><Form.Control type="number" value={amountMax} onChange={(e) => onChangeAmountMax(e.target.value)} placeholder="Max Amount" /></Col>
        <Col md={4}><Form.Control value={searchTerm} onChange={(e) => onChangeSearchTerm(e.target.value)} placeholder="Search SAP / Party / Project / Remarks" /></Col>
      </Row>

      {!drillRows.length ? (
        <p className="text-muted mb-0">No Cash EMD records found for applied filters.</p>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>SAP Doc No</th>
                <th>Date</th>
                <th>Month</th>
                <th>FY</th>
                <th>Final Name</th>
                <th>BID Project</th>
                <th>GL Code</th>
                <th>Amount</th>
                <th>Remarks</th>
                <th>Refund Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row, index) => (
                <tr key={`${row.id}-${index}`}>
                  <td>{row.sapDocNo}</td>
                  <td>{formatDate(row.parsedDate || row.docDate)}</td>
                  <td>{row.docMonth || row.monthLabel}</td>
                  <td>{row.fy}</td>
                  <td>{row.finalName}</td>
                  <td>{row.bidProject}</td>
                  <td>{row.glCode}</td>
                  <td>{formatAmount(row.amount)}</td>
                  <td>{row.remarks || '-'}</td>
                  <td><Badge bg={row.refundStatus === 'Refundable' ? 'success' : row.refundStatus === 'Pending' ? 'danger' : 'secondary'}>{row.refundStatus}</Badge></td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <small className="text-muted">
                Showing {(currentPageSafe - 1) * pageSize + 1}-{Math.min(currentPageSafe * pageSize, drillRows.length)} of {drillRows.length}
              </small>
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted">Rows per page</small>
                <Form.Select
                  size="sm"
                  style={{ width: 90 }}
                  value={pageSize}
                  onChange={(e) => onSetPageSize(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </div>
            </div>
            <Pagination size="sm" className="mb-0">
              <Pagination.First onClick={() => onSetCurrentPage(1)} disabled={currentPageSafe <= 1} />
              <Pagination.Prev onClick={() => onSetCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPageSafe <= 1} />
              <Pagination.Item active>{currentPageSafe}</Pagination.Item>
              <Pagination.Item disabled>{`/ ${totalPages}`}</Pagination.Item>
              <Pagination.Next onClick={() => onSetCurrentPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPageSafe >= totalPages} />
              <Pagination.Last onClick={() => onSetCurrentPage(totalPages)} disabled={currentPageSafe >= totalPages} />
            </Pagination>
          </div>
        </div>
      )}
    </Card.Body>
  </Card>
);

CashEmdTableCard.propTypes = {
  analytics: CashEmdAnalyticsPropType,
  tablePartyFilter: PropTypes.string.isRequired,
  onChangeTablePartyFilter: PropTypes.func.isRequired,
  tableProjectFilter: PropTypes.string.isRequired,
  onChangeTableProjectFilter: PropTypes.func.isRequired,
  amountMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChangeAmountMin: PropTypes.func.isRequired,
  amountMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChangeAmountMax: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onChangeSearchTerm: PropTypes.func.isRequired,
  drillRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  paginatedRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentPageSafe: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onSetPageSize: PropTypes.func.isRequired,
  onSetCurrentPage: PropTypes.func.isRequired
};

const CashEmdDashboardView = ({
  error,
  canSeePage,
  fyFilter,
  onChangeFyFilter,
  analytics,
  activeDataFilter,
  onClearDataFilter,
  kpiCards,
  onApplyDataFilter,
  isFilterTypeActive,
  showAddForm,
  setShowAddForm,
  addFormError,
  addFormSuccess,
  addFormData,
  onChangeAddFormField,
  onSubmitAddForm,
  addFormSubmitting,
  onResetAddFormMessages,
  tablePartyFilter,
  onChangeTablePartyFilter,
  tableProjectFilter,
  onChangeTableProjectFilter,
  amountMin,
  onChangeAmountMin,
  amountMax,
  onChangeAmountMax,
  searchTerm,
  onChangeSearchTerm,
  drillRows,
  paginatedRows,
  currentPageSafe,
  pageSize,
  totalPages,
  onSetPageSize,
  onSetCurrentPage
}) => (
  <Container fluid className="dashboard-page py-4">
    {error && <Alert variant="danger">{error}</Alert>}

    {!canSeePage ? (
      <Alert variant="warning">Cash EMD List is available only for Finance users and EXCO.</Alert>
    ) : (
      <>
        <CashEmdFiltersRow fyFilter={fyFilter} onChangeFy={onChangeFyFilter} analytics={analytics} />
        <CashEmdActiveDataFilterBanner activeDataFilter={activeDataFilter} onClear={onClearDataFilter} />
        <CashEmdKpiGrid kpiCards={kpiCards} activeDataFilter={activeDataFilter} onApplyDataFilter={onApplyDataFilter} />

        <CashEmdTrendSection analytics={analytics} isActive={isFilterTypeActive('month')} onApplyDataFilter={onApplyDataFilter} />
        <CashEmdPartySection analytics={analytics} isActive={isFilterTypeActive('party')} onApplyDataFilter={onApplyDataFilter} />
        <CashEmdProjectSection analytics={analytics} isActive={isFilterTypeActive('project')} onApplyDataFilter={onApplyDataFilter} />
        <CashEmdRefundAndGlSection
          analytics={analytics}
          isRefundActive={isFilterTypeActive('refundStatus')}
          isGlActive={isFilterTypeActive('gl')}
          isFyActive={isFilterTypeActive('fy')}
          onApplyDataFilter={onApplyDataFilter}
        />
        <CashEmdAddEntryCard
          showAddForm={showAddForm}
          setShowAddForm={setShowAddForm}
          addFormError={addFormError}
          addFormSuccess={addFormSuccess}
          addFormData={addFormData}
          onChangeField={onChangeAddFormField}
          onSubmit={onSubmitAddForm}
          addFormSubmitting={addFormSubmitting}
          onResetMessages={onResetAddFormMessages}
        />
        <CashEmdTableCard
          analytics={analytics}
          tablePartyFilter={tablePartyFilter}
          onChangeTablePartyFilter={onChangeTablePartyFilter}
          tableProjectFilter={tableProjectFilter}
          onChangeTableProjectFilter={onChangeTableProjectFilter}
          amountMin={amountMin}
          onChangeAmountMin={onChangeAmountMin}
          amountMax={amountMax}
          onChangeAmountMax={onChangeAmountMax}
          searchTerm={searchTerm}
          onChangeSearchTerm={onChangeSearchTerm}
          drillRows={drillRows}
          paginatedRows={paginatedRows}
          currentPageSafe={currentPageSafe}
          pageSize={pageSize}
          totalPages={totalPages}
          onSetPageSize={onSetPageSize}
          onSetCurrentPage={onSetCurrentPage}
        />
      </>
    )}
  </Container>
);

CashEmdDashboardView.propTypes = {
  error: PropTypes.string,
  canSeePage: PropTypes.bool.isRequired,
  fyFilter: PropTypes.string.isRequired,
  onChangeFyFilter: PropTypes.func.isRequired,
  analytics: CashEmdAnalyticsPropType.isRequired,
  activeDataFilter: CashEmdActiveDataFilterPropType.isRequired,
  onClearDataFilter: PropTypes.func.isRequired,
  kpiCards: PropTypes.arrayOf(CashEmdKpiCardPropType).isRequired,
  onApplyDataFilter: PropTypes.func.isRequired,
  isFilterTypeActive: PropTypes.func.isRequired,
  showAddForm: PropTypes.bool.isRequired,
  setShowAddForm: PropTypes.func.isRequired,
  addFormError: PropTypes.string.isRequired,
  addFormSuccess: PropTypes.string.isRequired,
  addFormData: CashEmdAddFormDataPropType.isRequired,
  onChangeAddFormField: PropTypes.func.isRequired,
  onSubmitAddForm: PropTypes.func.isRequired,
  addFormSubmitting: PropTypes.bool.isRequired,
  onResetAddFormMessages: PropTypes.func.isRequired,
  tablePartyFilter: PropTypes.string.isRequired,
  onChangeTablePartyFilter: PropTypes.func.isRequired,
  tableProjectFilter: PropTypes.string.isRequired,
  onChangeTableProjectFilter: PropTypes.func.isRequired,
  amountMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChangeAmountMin: PropTypes.func.isRequired,
  amountMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChangeAmountMax: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onChangeSearchTerm: PropTypes.func.isRequired,
  drillRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  paginatedRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentPageSafe: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onSetPageSize: PropTypes.func.isRequired,
  onSetCurrentPage: PropTypes.func.isRequired
};

const CashEmdDashboardPage = ({ departmentId = null }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canSeePage, setCanSeePage] = useState(false);
  const [fyFilter, setFyFilter] = useState('all');
  const [tablePartyFilter, setTablePartyFilter] = useState('all');
  const [tableProjectFilter, setTableProjectFilter] = useState('all');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeDataFilter, setActiveDataFilter] = useState({ type: 'all', value: null, label: 'All Rows' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormSubmitting, setAddFormSubmitting] = useState(false);
  const [addFormError, setAddFormError] = useState('');
  const [addFormSuccess, setAddFormSuccess] = useState('');
  const [addFormData, setAddFormData] = useState(INITIAL_CASH_EMD_FORM);

  const applyDataFilter = (type, value, label) => {
    setActiveDataFilter({ type, value, label });
  };

  const clearDataFilter = () => {
    setActiveDataFilter({ type: 'all', value: null, label: 'All Rows' });
  };

  const isFilterTypeActive = (...types) => types.includes(activeDataFilter.type);

  const analytics = useMemo(() => computeCashEmdAnalytics({ rows, fyFilter }), [rows, fyFilter]);

  const kpiCards = useMemo(() => ([
    { key: 'total', title: 'Cash Currently Blocked (INR)', value: formatAmount(analytics.totalAmount), tooltip: 'Total blocked cash = SUM(Amount) for selected FY scope.', filterType: 'all', filterValue: null },
    { key: 'docs', title: 'Total EMD Entries', value: analytics.totalDocs, tooltip: 'Total entries counted from SAP Doc records in selected FY scope.', filterType: 'all', filterValue: null },
    { key: 'refund', title: 'Likely Recoverable Amount (INR)', value: formatAmount(analytics.refundableAmount), tooltip: "SUM(Amount) where Remarks contains 'Refundable'/'Refund'/'EMD'.", filterType: 'refundable', filterValue: null },
    { key: 'bids', title: 'Active Bid Lines', value: analytics.activeBidsCount, tooltip: 'Distinct count of BID Project in selected FY scope.', filterType: 'hasBid', filterValue: null },
    { key: 'parties', title: 'Unique Counterparties', value: analytics.uniquePartiesCount, tooltip: 'Distinct count of Final Name in selected FY scope.', filterType: 'all', filterValue: null },
    { key: 'current-fy', title: 'Current FY Cash Blocked (INR)', value: formatAmount(analytics.currentFyAmount), tooltip: `SUM(Amount) only for ${analytics.currentFY}.`, filterType: 'fy', filterValue: analytics.currentFY }
  ]), [analytics]);

  const drillRows = useMemo(() => computeCashEmdDrillRows({
    scopedRows: analytics.scopedRows,
    activeDataFilter,
    tablePartyFilter,
    tableProjectFilter,
    amountMin,
    amountMax,
    searchTerm
  }), [analytics.scopedRows, tablePartyFilter, tableProjectFilter, amountMin, amountMax, searchTerm, activeDataFilter]);

  const totalPages = useMemo(() => {
    if (!drillRows.length) return 1;
    return Math.max(1, Math.ceil(drillRows.length / pageSize));
  }, [drillRows.length, pageSize]);

  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (currentPageSafe - 1) * pageSize;
    return drillRows.slice(start, start + pageSize);
  }, [drillRows, currentPageSafe, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [fyFilter, tablePartyFilter, tableProjectFilter, amountMin, amountMax, searchTerm, activeDataFilter, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const fetchCashEmdRows = useCallback(async () => {
    if (!departmentId) {
      setError('Department is required to load Cash EMD List.');
      setCanSeePage(true);
      setRows([]);
      return;
    }

    const response = await fileAPI.getCashEmdList({ departmentId });
    const nextRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
    setRows(nextRows);
    setCanSeePage(true);
  }, [departmentId]);

  const handleAddFormChange = (field, value) => {
    setAddFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateCashEmdRow = async (event) => {
    event.preventDefault();
    setAddFormError('');
    setAddFormSuccess('');

    if (!departmentId) {
      setAddFormError('Department context is required.');
      return;
    }

    try {
      setAddFormSubmitting(true);
      await fileAPI.createCashEmdRow({
        departmentId,
        ...addFormData
      });
      setAddFormSuccess('Cash EMD entry added successfully.');
      setAddFormData(INITIAL_CASH_EMD_FORM);
      await fetchCashEmdRows();
    } catch (err) {
      setAddFormError(err?.message || 'Failed to add Cash EMD entry.');
    } finally {
      setAddFormSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        await fetchCashEmdRows();
      } catch (err) {
        const message = err?.message || 'Failed to load Cash EMD List.';
        if (message.includes('403')) setCanSeePage(false);
        else setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchCashEmdRows]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0">Loading cash EMD table...</p>
      </div>
    );
  }

  return (
    <CashEmdDashboardView
      error={error}
      canSeePage={canSeePage}
      fyFilter={fyFilter}
      onChangeFyFilter={setFyFilter}
      analytics={analytics}
      activeDataFilter={activeDataFilter}
      onClearDataFilter={clearDataFilter}
      kpiCards={kpiCards}
      onApplyDataFilter={applyDataFilter}
      isFilterTypeActive={isFilterTypeActive}
      showAddForm={showAddForm}
      setShowAddForm={setShowAddForm}
      addFormError={addFormError}
      addFormSuccess={addFormSuccess}
      addFormData={addFormData}
      onChangeAddFormField={handleAddFormChange}
      onSubmitAddForm={handleCreateCashEmdRow}
      addFormSubmitting={addFormSubmitting}
      onResetAddFormMessages={() => {
        setAddFormError('');
        setAddFormSuccess('');
      }}
      tablePartyFilter={tablePartyFilter}
      onChangeTablePartyFilter={setTablePartyFilter}
      tableProjectFilter={tableProjectFilter}
      onChangeTableProjectFilter={setTableProjectFilter}
      amountMin={amountMin}
      onChangeAmountMin={setAmountMin}
      amountMax={amountMax}
      onChangeAmountMax={setAmountMax}
      searchTerm={searchTerm}
      onChangeSearchTerm={setSearchTerm}
      drillRows={drillRows}
      paginatedRows={paginatedRows}
      currentPageSafe={currentPageSafe}
      pageSize={pageSize}
      totalPages={totalPages}
      onSetPageSize={setPageSize}
      onSetCurrentPage={setCurrentPage}
    />
  );
};

CashEmdDashboardPage.propTypes = {
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default CashEmdDashboardPage;
